// Pure logic for the advertiser/partner provisioning panel, kept out of the
// components so the window arithmetic, the submit gates and the quota/expiry
// formatting can be unit tested without rendering anything.
//
// The vocabulary here is advertiser/partner, never "guest" — ADR-049 owns that
// word for broadcast guests. The one place "guest" appears at all is the
// capability enum's own name, which belongs to protos.

/**
 * MIN_REASON_LENGTH matches admin-service's own minReasonLength, so a short
 * reason is caught before the round trip rather than coming back as a 400.
 *
 * A reason is mandatory on BOTH grant and revoke here. On the server it is
 * mandatory too — deliberately stricter than auth-service, which accepts an
 * empty reason on either. This is the audit trail for who was allowed to
 * publish sponsored content and why; a blank one is worse than useless.
 */
export const MIN_REASON_LENGTH = 3;

/**
 * WindowChoice models the grant form's duration control.
 *
 * Unlike the scholarship sheet's equivalent there is NO "permanent" variant,
 * and that absence is the point: a standing, never-expiring permission to
 * publish sponsored content is exactly the kind of grant that outlives the
 * commercial arrangement behind it and gets discovered years later. The server
 * rejects a missing expiry for the same reason. Nothing here can express one.
 */
export type WindowChoice =
  | { kind: "preset"; days: (typeof WINDOW_PRESET_DAYS)[number] }
  | { kind: "custom"; date: Date };

export const WINDOW_PRESET_DAYS = [30, 90, 180, 365] as const;

export function windowPresetLabel(days: number): string {
  if (days === 30) return "30 days";
  if (days === 365) return "1 year";
  return `${days} days`;
}

/**
 * Resolves a window choice to an absolute instant. This is the ONLY place
 * clock arithmetic happens: the server does none — it takes an RFC3339
 * instant and passes it through to auth-service, exactly as the scholarship
 * grant path does.
 *
 * Preset days are added in UTC so a grant issued near a DST boundary lasts the
 * number of days it says on the chip.
 */
export function resolveWindowToInstant(choice: WindowChoice, now: Date = new Date()): Date {
  if (choice.kind === "custom") return choice.date;
  const resolved = new Date(now);
  resolved.setUTCDate(resolved.getUTCDate() + choice.days);
  return resolved;
}

export interface GrantFormState {
  /** Runtime-resolved principal id, from a live search. Never a constant. */
  principalId: string | null;
  reason: string;
  choice: WindowChoice | null;
  /** Raw text from the quota input, so "" and "0" stay distinguishable. */
  quotaInput: string;
  unmetered: boolean;
}

/**
 * parseQuotaLimit turns the quota input into the int the API wants, or null
 * when the input is not a usable quota.
 *
 * Unmetered is expressed by the CHECKBOX, not by typing 0, even though the
 * wire format uses 0 for it. Those two are opposites to a reader — "0 posts
 * allowed" vs "no limit" — and a form where typing 0 silently means unlimited
 * is a form that eventually grants unlimited by accident.
 */
export function parseQuotaLimit(
  state: Pick<GrantFormState, "quotaInput" | "unmetered">,
): number | null {
  if (state.unmetered) return 0;
  const trimmed = state.quotaInput.trim();
  if (trimmed === "") return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value < 1) return null;
  return value;
}

/**
 * canSubmitGrant is the single source of truth for the grant button's enabled
 * state. Every condition here is also enforced server-side; this exists so the
 * operator learns about a problem while they can still fix it in the form.
 */
export function canSubmitGrant(state: GrantFormState): boolean {
  if (!state.principalId) return false;
  if (state.reason.trim().length < MIN_REASON_LENGTH) return false;
  if (state.choice === null) return false;
  if (state.choice.kind === "custom" && Number.isNaN(state.choice.date.getTime())) return false;
  if (parseQuotaLimit(state) === null) return false;
  return true;
}

const CONSEQUENCE_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * formatGrantConsequence is the live "what will actually happen" line above the
 * submit button: how many posts, until when. Two facts, no prose.
 *
 * The date can be Invalid here — the custom chip sets `new Date(NaN)` the
 * moment it is clicked, before a date has been picked — and
 * Intl.DateTimeFormat throws on that, so it is handled explicitly rather than
 * left to whichever caller remembers.
 */
export function formatGrantConsequence(state: GrantFormState): string {
  if (state.choice === null) return "Choose how long this grant lasts.";
  const expiresAt = resolveWindowToInstant(state.choice);
  if (Number.isNaN(expiresAt.getTime())) return "Pick a date to see when the grant ends.";

  const quota = parseQuotaLimit(state);
  const allowance =
    quota === null
      ? "An unset number of posts"
      : quota === 0
        ? "Unlimited posts"
        : `${quota} post${quota === 1 ? "" : "s"}`;

  return `${allowance} until ${CONSEQUENCE_DATE_FORMAT.format(expiresAt)}.`;
}

/** GrantLike is the minimal shape the formatters need, kept structural so this
 * pure module does not import the data layer. */
export interface GrantLike {
  quotaLimit: number;
  quotaUsed: number;
  isUnmetered: boolean;
  quotaRemaining?: number;
  expiresAt?: string;
  status: string;
}

/**
 * daysRemaining is calendar days from now until `date`, floored — the same
 * convention the scholarships view uses, so "3 days left" means the same thing
 * on both screens.
 */
export function daysRemaining(date: Date, now: Date = new Date()): number {
  return Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatQuota(grant: GrantLike): string {
  if (grant.isUnmetered) return `${grant.quotaUsed} used · unmetered`;
  return `${grant.quotaUsed} / ${grant.quotaLimit} used`;
}

/**
 * quotaFraction drives the usage meter. Returns null for an unmetered grant,
 * where a proportion of "no limit" is not a thing. Clamped to 1 so an
 * overspent grant (quota_used > quota_limit, which auth-service permits since
 * enforcement is at the point of use) renders a full bar rather than
 * overflowing its track.
 */
export function quotaFraction(grant: GrantLike): number | null {
  if (grant.isUnmetered || grant.quotaLimit <= 0) return null;
  return Math.min(1, grant.quotaUsed / grant.quotaLimit);
}

const EXPIRY_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * formatExpiry is the expiry cell. It says both the date AND the distance,
 * because either alone fails an operator: a bare date makes them do arithmetic
 * during an incident, and a bare "in 4 days" hides which day to put in the
 * calendar.
 *
 * A revoked grant reports its revocation instead — its expiry is no longer the
 * fact that matters about it.
 */
export function formatExpiry(grant: GrantLike, now: Date = new Date()): string {
  if (grant.status === "revoked") return "Revoked";
  if (!grant.expiresAt) return "No end date";
  const expiresAt = new Date(grant.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return "No end date";

  const date = EXPIRY_DATE_FORMAT.format(expiresAt);
  const remaining = daysRemaining(expiresAt, now);
  if (remaining < 0) return `${date} · expired`;
  if (remaining === 0) return `${date} · today`;
  return `${date} · ${remaining} day${remaining === 1 ? "" : "s"} left`;
}

/** Human label for a grant's derived status. */
export function statusLabel(status: string): string {
  switch (status) {
    case "active":
      return "Active";
    case "scheduled":
      return "Scheduled";
    case "expired":
      return "Expired";
    case "revoked":
      return "Revoked";
    case "exhausted":
      return "Quota spent";
    default:
      return status;
  }
}

/**
 * AdjustFormState models the "Adjust quota / expiry" dialog. Both controls
 * are OPTIONAL and independent — the operator may raise the quota, move the
 * expiry, or both — and each one's "leave this alone" state has its own
 * unambiguous representation:
 *
 *   - quotaInput === "" (and unmetered === false) means "don't touch the
 *     quota". unmetered checked means "set it to 0 (unmetered)", same
 *     convention as GrantFormState.
 *   - choice === null means "don't touch the expiry". A choice, once made,
 *     resolves to an absolute instant exactly like the grant form's window —
 *     see resolveWindowToInstant.
 *
 * reason is mandatory, same as everywhere else in this panel.
 */
export interface AdjustFormState {
  quotaInput: string;
  unmetered: boolean;
  choice: WindowChoice | null;
  reason: string;
}

export const emptyAdjustForm: AdjustFormState = {
  quotaInput: "",
  unmetered: false,
  choice: null,
  reason: "",
};

/**
 * parseAdjustQuotaLimit resolves the adjust form's quota control to what the
 * API wants: `undefined` when the operator left it alone (nothing is sent),
 * 0 for the unmetered checkbox, the typed integer otherwise, or `null` when
 * the input is not a valid non-negative integer.
 *
 * Unlike parseQuotaLimit (the grant form's equivalent, which rejects a typed
 * "0" because on THAT form a blank field already means "invalid" and 0 would
 * be indistinguishable from it without the checkbox) a typed "0" here is
 * unambiguous — blank already means "no change" — so it is accepted directly
 * as well as via the checkbox.
 */
export function parseAdjustQuotaLimit(
  state: Pick<AdjustFormState, "quotaInput" | "unmetered">,
): number | null | undefined {
  if (state.unmetered) return 0;
  const trimmed = state.quotaInput.trim();
  if (trimmed === "") return undefined;
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value)) return null;
  return value;
}

/**
 * quotaBelowUsed is the client-side hint — never authoritative, see
 * canSubmitAdjust — that the chosen limit would sit below the grant's quota
 * already used. auth-service enforces this itself (AdjustPrincipalCapability
 * Grant requires quota_limit >= quota_used); this exists purely so an
 * operator sees the problem before submitting rather than after a 400. 0
 * (unmetered) is never "below" anything.
 */
export function quotaBelowUsed(
  limit: number | null | undefined,
  grant: Pick<GrantLike, "quotaUsed">,
): boolean {
  if (limit === undefined || limit === null || limit === 0) return false;
  return limit < grant.quotaUsed;
}

/**
 * canSubmitAdjust is the single source of truth for the adjust dialog's
 * confirm button. Every condition here is also enforced server-side; this
 * exists so the operator learns about a problem while they can still fix it.
 */
export function canSubmitAdjust(
  state: AdjustFormState,
  grant: Pick<GrantLike, "quotaUsed">,
): boolean {
  if (state.reason.trim().length < MIN_REASON_LENGTH) return false;

  const limit = parseAdjustQuotaLimit(state);
  if (limit === null) return false;

  let expiresAt: Date | undefined;
  if (state.choice !== null) {
    expiresAt = resolveWindowToInstant(state.choice);
    if (Number.isNaN(expiresAt.getTime())) return false;
  }

  // Nothing to adjust: the server rejects this too, but there is no reason to
  // make a round trip find that out.
  if (limit === undefined && expiresAt === undefined) return false;

  if (quotaBelowUsed(limit, grant)) return false;

  return true;
}

/**
 * normalizeHandle mirrors admin-service's own normalisation of every handle
 * field it accepts (handleCreateAdvertiser's username,
 * handleSetAdvertiserHandle's handle): lowercased, an optional leading "@"
 * stripped. Applied both to what a form validates and to what is sent, so the
 * value user-service checks for uniqueness is exactly what the operator meant,
 * whether or not they typed the @.
 */
export function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@/, "").toLowerCase();
}

/**
 * needsAttention marks the rows an operator should look at first: a live grant
 * that has run out of quota, or one inside its last week.
 *
 * "Quota spent while the window is still open" is the state where the
 * adjust-quota action is the actual remedy — see the row action menu.
 */
export function needsAttention(grant: GrantLike, now: Date = new Date()): boolean {
  if (grant.status === "exhausted") return true;
  if (grant.status !== "active") return false;
  if (!grant.expiresAt) return false;
  const expiresAt = new Date(grant.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false;
  const remaining = daysRemaining(expiresAt, now);
  return remaining >= 0 && remaining <= 7;
}

// --- The advertisers list ----------------------------------------------------
//
// The landing view is ONE ROW PER ADVERTISER, not one row per grant. That is
// the whole point of it: an operator who provisioned an account last week and
// cannot find it again is looking for a person, not for a row in
// auth-service's grant table. A principal with three grants is one advertiser
// with a history, and a principal whose only grant lapsed is still an
// advertiser — just not a working one.
//
// Everything below is derived from data the panel can already fetch. No new
// read was invented for it; where a fact is genuinely unavailable the
// derivation says so (see AdvertiserStatus's "unknown") rather than guessing.

/**
 * PrincipalGrantLike is the structural shape the list derivation needs from a
 * grant row. Kept structural, like GrantLike above, so this module never
 * imports the data layer.
 */
export interface PrincipalGrantLike extends GrantLike {
  id: string;
  principalId: string;
  principalType?: string;
  /** Resolved server-side from user-service; absent when unresolvable. */
  principalHandle?: string;
  principalName?: string;
  createdAt?: string;
}

/**
 * AdvertiserStatus is the one word the list badges each advertiser with.
 *
 * "unknown" is not a state anything is in. It is what this derivation reports
 * when it could not tell — see deriveAdvertiserStatus. A USER principal with
 * no resolved profile also reports "unknown": admin-service's Register now
 * creates the profile at account creation, so a bare row no longer means
 * "not verified yet" — it means this derivation has nothing to say about the
 * account (see emailVerificationKnown in admin-service's advertiser_grants.go).
 */
export type AdvertiserStatus =
  | "active"
  | "expiring-soon"
  | "scheduled"
  | "exhausted"
  | "expired"
  | "revoked"
  | "unknown";

/** EXPIRING_SOON_DAYS is the window that turns "active" into "expiring soon".
 * Same seven days needsAttention uses, so the badge and the row highlight
 * cannot disagree about which grants are nearly over. */
export const EXPIRING_SOON_DAYS = 7;

export interface AdvertiserRow<G extends PrincipalGrantLike = PrincipalGrantLike> {
  principalId: string;
  handle?: string;
  displayName?: string;
  /** Every grant for this principal, governing one first. */
  grants: G[];
  /** The grant the row's status, quota and expiry columns describe. */
  governing: G;
  status: AdvertiserStatus;
  /**
   * Whether this principal carries content-service's sponsored-links
   * disclosure flag. `undefined` means the allowlist could not be read — NOT
   * that the flag is off. Absent and off are different, and rendering the
   * first as the second would send an operator to re-grant something that is
   * already granted.
   */
  sponsoredLinks?: boolean;
}

/**
 * GRANT_PRECEDENCE ranks a principal's grants so the row describes the one
 * that decides what they can do right now. A live grant always speaks for the
 * account; a spent one speaks louder than an expired one (its remedy is
 * different); a revoked one speaks only when there is nothing else.
 */
const GRANT_PRECEDENCE: Record<string, number> = {
  active: 0,
  scheduled: 1,
  exhausted: 2,
  expired: 3,
  revoked: 4,
};

function grantRank(grant: PrincipalGrantLike): number {
  return GRANT_PRECEDENCE[grant.status] ?? 5;
}

function grantInstant(grant: PrincipalGrantLike): number {
  const raw = grant.expiresAt ?? grant.createdAt;
  if (!raw) return 0;
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** compareGrants orders a principal's grants: most authoritative first, and
 * within a tier the one that runs longest. */
export function compareGrants(a: PrincipalGrantLike, b: PrincipalGrantLike): number {
  const byRank = grantRank(a) - grantRank(b);
  if (byRank !== 0) return byRank;
  return grantInstant(b) - grantInstant(a);
}

/**
 * deriveAdvertiserStatus collapses an account fact and a grant state into the
 * row's badge.
 *
 * # WHY A BARE USER ROW IS "unknown", NOT A VERIFICATION CLAIM
 *
 * admin-service resolves a grant's principal to a handle/display name with one
 * best-effort user-service lookup. A USER principal that comes back with
 * neither has no resolved profile — that used to mean "not verified yet",
 * back when a profile was created only at email verification. It no longer
 * does: admin-service's Register now creates the profile at account creation
 * (admin-service#56), so profile presence says nothing about verification in
 * either direction, and this derivation has nothing sound to conclude beyond
 * "unknown" — whether the bare row is one this lookup could not resolve, or
 * one where resolution ran and genuinely found nothing.
 *
 * SQUAD principals are excluded from the inference entirely: admin-service
 * resolves only USER principals, so a squad's bare row means "not looked up".
 */
export function deriveAdvertiserStatus(
  governing: PrincipalGrantLike,
  options: { identitiesResolved: boolean; now?: Date },
): { status: AdvertiserStatus } {
  const now = options.now ?? new Date();
  const isUserPrincipal =
    !governing.principalType || governing.principalType === "PRINCIPAL_TYPE_USER";
  const hasProfile =
    (governing.principalHandle ?? "").trim() !== "" ||
    (governing.principalName ?? "").trim() !== "";

  if (isUserPrincipal && !hasProfile) {
    return { status: "unknown" };
  }

  switch (governing.status) {
    case "active":
      return { status: expiringSoon(governing, now) ? "expiring-soon" : "active" };
    case "scheduled":
      return { status: "scheduled" };
    case "exhausted":
      return { status: "exhausted" };
    case "expired":
      return { status: "expired" };
    case "revoked":
      return { status: "revoked" };
    default:
      // A status this build does not know about is reported as unknown rather
      // than quietly rendered as one of the states it is not.
      return { status: "unknown" };
  }
}

function expiringSoon(grant: PrincipalGrantLike, now: Date): boolean {
  if (!grant.expiresAt) return false;
  const expiresAt = new Date(grant.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return false;
  const remaining = daysRemaining(expiresAt, now);
  return remaining >= 0 && remaining <= EXPIRING_SOON_DAYS;
}

export interface GroupAdvertisersOptions {
  /** Whether admin-service's identity lookup ran and succeeded — see
   * deriveAdvertiserStatus for why this is load-bearing. */
  identitiesResolved: boolean;
  /**
   * Principal ids carrying content-service's sponsored-links flag, or null
   * when the allowlist could not be read at all. Null and empty are different:
   * empty means nobody has it, null means we do not know.
   */
  sponsoredLinkPublisherIds?: ReadonlySet<string> | null;
  now?: Date;
}

/**
 * groupAdvertisers turns a flat list of capability grants into one row per
 * advertiser principal, in the order an operator wants to work through them:
 * the ones needing attention first, then the live ones, then the finished
 * ones, alphabetical within each tier so the list does not reshuffle between
 * loads.
 */
export function groupAdvertisers<G extends PrincipalGrantLike>(
  grants: G[],
  options: GroupAdvertisersOptions,
): AdvertiserRow<G>[] {
  const now = options.now ?? new Date();

  const byPrincipal = new Map<string, G[]>();
  for (const grant of grants) {
    const principalId = grant.principalId?.trim();
    if (!principalId) continue;
    const bucket = byPrincipal.get(principalId);
    if (bucket) bucket.push(grant);
    else byPrincipal.set(principalId, [grant]);
  }

  const rows: AdvertiserRow<G>[] = [];
  for (const [principalId, bucket] of byPrincipal) {
    const ordered = [...bucket].sort(compareGrants);
    const governing = ordered[0];
    const { status } = deriveAdvertiserStatus(governing, {
      identitiesResolved: options.identitiesResolved,
      now,
    });

    // The identity comes from whichever grant carries it: admin-service
    // resolves every row from the same batch, but a partial resolution should
    // still name the account rather than showing a UUID next to a name.
    const named = ordered.find(
      (grant) => (grant.principalHandle ?? "") !== "" || (grant.principalName ?? "") !== "",
    );

    rows.push({
      principalId,
      handle: named?.principalHandle || undefined,
      displayName: named?.principalName || undefined,
      grants: ordered,
      governing,
      status,
      sponsoredLinks: options.sponsoredLinkPublisherIds
        ? options.sponsoredLinkPublisherIds.has(principalId)
        : undefined,
    });
  }

  return rows.sort(compareAdvertiserRows);
}

/** ROW_ORDER puts the rows an operator has to do something about at the top.
 * Expiring-soon leads because it is the state closest to lapsing on its own. */
const ROW_ORDER: Record<AdvertiserStatus, number> = {
  "expiring-soon": 0,
  exhausted: 1,
  active: 2,
  scheduled: 3,
  unknown: 4,
  expired: 5,
  revoked: 6,
};

export function compareAdvertiserRows(a: AdvertiserRow, b: AdvertiserRow): number {
  const byStatus = ROW_ORDER[a.status] - ROW_ORDER[b.status];
  if (byStatus !== 0) return byStatus;
  return advertiserLabel(a).localeCompare(advertiserLabel(b));
}

/** advertiserLabel is what the row calls this advertiser: their brand name,
 * else their @handle, else the raw id — which is not a fallback to be
 * embarrassed about, it is the only thing that exists for an account that has
 * not verified yet. */
export function advertiserLabel(
  row: Pick<AdvertiserRow, "displayName" | "handle" | "principalId">,
): string {
  if (row.displayName) return row.displayName;
  if (row.handle) return `@${row.handle}`;
  return row.principalId;
}

export function advertiserStatusLabel(status: AdvertiserStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "expiring-soon":
      return "Expiring soon";
    case "scheduled":
      return "Scheduled";
    case "exhausted":
      return "Quota spent";
    case "expired":
      return "Expired";
    case "revoked":
      return "Revoked";
    default:
      return "Unknown";
  }
}

/**
 * formatAdvertiserSummary is the row's one-line "so what": what the badge
 * means for this particular advertiser, in the terms the operator would ask
 * about. It never repeats the badge word back.
 */
export function formatAdvertiserSummary(row: AdvertiserRow, now: Date = new Date()): string {
  if (row.status === "unknown") {
    return "Account details unavailable. Showing grant data only.";
  }
  if (row.status === "revoked") return "No live grant.";
  if (row.status === "expired") return "The window closed.";
  if (row.status === "exhausted")
    return `${formatQuota(row.governing)}. Raise the allowance to continue.`;

  const expiry = formatExpiry(row.governing, now);
  return `${formatQuota(row.governing)} · ${expiry}`;
}

/** advertiserNeedsAttention drives the row highlight. Deliberately the same
 * question the badge answers, so a highlighted row always has a badge that
 * explains it. */
export function advertiserNeedsAttention(row: AdvertiserRow): boolean {
  return row.status === "expiring-soon" || row.status === "exhausted";
}

// --- The new-advertiser wizard ----------------------------------------------
//
// One form, then one chain of three calls run in order behind a single
// spinner — no per-step display. The chain is sequential because each step
// needs the id the step before it produced, and it is RESUMABLE because it is
// three calls against three different systems with no transaction between
// them: any of them can fail on its own, and when one does the operator sees
// ONE inline error naming what failed, with one retry that resumes from
// there — not start again.
//
// # THE STEP THAT MUST NEVER RUN TWICE
//
// auth-service's Register is NOT idempotent: calling it again with the same
// email fails, and calling it again with a different one silently creates a
// SECOND account that nothing will ever link to the first. So the account step
// is guarded twice — the sequencer skips any step already marked done, and the
// step's own runner refuses to call the API once a user id exists. The second
// guard is the one that survives a refactor of the first.

export const WIZARD_STEPS = ["account", "grant", "sponsoredLinks"] as const;
export type WizardStepId = (typeof WIZARD_STEPS)[number];
export type WizardStepState = "pending" | "running" | "done" | "failed";

/** The form field a step's failure belongs to, when it belongs to one. Drives
 * the inline fix — an error an operator can correct in place beats an error
 * they can only read. */
export type WizardFieldKey = "email" | "handle" | "allowance" | "reason";

export interface WizardStepStatus {
  state: WizardStepState;
  error: string | null;
  field: WizardFieldKey | null;
}

export type WizardProgress = Record<WizardStepId, WizardStepStatus>;

export function initialWizardProgress(): WizardProgress {
  return {
    account: { state: "pending", error: null, field: null },
    grant: { state: "pending", error: null, field: null },
    sponsoredLinks: { state: "pending", error: null, field: null },
  };
}

/** wizardStepName is the short, factual name of which call failed — used only
 * in an inline error ("Grant failed: …"), never as a visible step list. */
function wizardStepName(id: WizardStepId): string {
  switch (id) {
    case "account":
      return "Account";
    case "grant":
      return "Grant";
    case "sponsoredLinks":
      return "Sponsored links";
  }
}

/**
 * stepsToRunFrom returns the steps a run should execute, starting at `from`
 * and skipping everything already done.
 *
 * This is the sequencer half of the double-run guard. A retry starts at the
 * step that failed; a first run starts at the beginning. Either way a step
 * marked done is never in the returned list, so a retry of step two can never
 * re-create the account step one already made.
 */
export function stepsToRunFrom(progress: WizardProgress, from: WizardStepId): WizardStepId[] {
  const startIndex = WIZARD_STEPS.indexOf(from);
  if (startIndex < 0) return [];
  return WIZARD_STEPS.slice(startIndex).filter((id) => progress[id].state !== "done");
}

/** wizardFailedStep names the step to retry, if any. */
export function wizardFailedStep(progress: WizardProgress): WizardStepId | null {
  return WIZARD_STEPS.find((id) => progress[id].state === "failed") ?? null;
}

/** wizardSettled reports that the chain has stopped — every step is either
 * done or one has failed. Used to decide when to show the handover screen (all
 * done) versus the retry affordance. */
export function wizardSettled(progress: WizardProgress): boolean {
  return !WIZARD_STEPS.some(
    (id) => progress[id].state === "running" || progress[id].state === "pending",
  );
}

export function wizardSucceeded(progress: WizardProgress): boolean {
  return WIZARD_STEPS.every((id) => progress[id].state === "done");
}

/**
 * mapAdvertiserError turns a server message into the ONE inline error the
 * wizard shows for a failed step, and says which field to fix when the
 * answer is "fix a field".
 *
 * A conflict on the account step is a taken email or a taken handle — not
 * the same fix, so the message says which: "Email taken." or "Handle taken."
 * on the field itself, decided by whether the server's own message mentions
 * "handle"/"username" (admin-service's request field name) or defaults to
 * email otherwise. A conflict on the grant step means this principal already
 * has one, so the message says that. Anything else falls through to the
 * server's own message, prefixed with which of the three calls it was —
 * there is no visible step list any more, so the operator has no other way
 * to tell account/grant/sponsoredLinks apart.
 */
export function mapAdvertiserError(
  step: WizardStepId,
  error: unknown,
): { message: string; field: WizardFieldKey | null } {
  const raw = error instanceof Error ? error.message : "";
  const message = raw.trim() || "The request failed.";
  const conflict = /already exist|already has|taken|duplicate|conflict/i.test(message);

  if (step === "account" && conflict) {
    const aboutHandle = /handle|username/i.test(message);
    return {
      message: aboutHandle ? "Handle taken." : "Email taken.",
      field: aboutHandle ? "handle" : "email",
    };
  }
  if (step === "grant" && conflict) {
    return { message: "Grant failed: already active.", field: null };
  }
  // Server messages arrive with no guaranteed trailing punctuation; this is
  // the one place a sentence fragment is stitched onto a fixed prefix, so it
  // is the one place that punctuation is normalised.
  const detail = message.endsWith(".") ? message : `${message}.`;
  return { message: `${wizardStepName(step)} failed: ${detail}`, field: null };
}

export interface WizardFormState {
  email: string;
  /** The brand name. It IS the account's display name: there is no squad and
   * no separate brand record — publishes attribute to the user. */
  brandName: string;
  handle: string;
  quotaInput: string;
  unmetered: boolean;
  choice: WindowChoice | null;
  reason: string;
}

export const emptyWizardForm: WizardFormState = {
  email: "",
  brandName: "",
  handle: "",
  quotaInput: "",
  unmetered: false,
  choice: null,
  reason: "",
};

/**
 * looksLikeEmail mirrors what admin-service accepts — a bare address, not
 * "Name <addr>" — so a typo is caught in the form instead of coming back as a
 * 400 after the operator has filled in everything else.
 */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed !== value.trim().replace(/\s/g, "")) return false;
  return /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(trimmed);
}

/** canSubmitWizard is the single source of truth for the wizard's submit
 * button. Every condition is enforced server-side too; this exists so the
 * operator learns about a problem before a three-call chain starts. */
export function canSubmitWizard(state: WizardFormState): boolean {
  if (!looksLikeEmail(state.email)) return false;
  if (state.brandName.trim().length === 0) return false;
  if (normalizeHandle(state.handle).length === 0) return false;
  if (state.choice === null) return false;
  if (state.choice.kind === "custom" && Number.isNaN(state.choice.date.getTime())) return false;
  if (parseQuotaLimit(state) === null) return false;
  if (state.reason.trim().length < MIN_REASON_LENGTH) return false;
  return true;
}

/**
 * formatAllowance says the post allowance the way the handover blurb needs it
 * — as a noun phrase, not as a number the reader has to interpret.
 */
export function formatAllowance(quotaLimit: number): string {
  if (quotaLimit === 0) return "an unmetered posting allowance";
  return `${quotaLimit} post${quotaLimit === 1 ? "" : "s"}`;
}

const BLURB_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export interface HandoverBlurbParams {
  email: string;
  handle: string;
  brandName: string;
  siteUrl: string;
  studioUrl: string;
  quotaLimit: number;
  expiresAt: Date;
}

/**
 * handoverBlurb is the message the operator sends the advertiser — everything
 * they need in the order they need it, and nothing the operator would have to
 * edit out.
 *
 * It deliberately does NOT contain the temporary password. The password is
 * shown once, in its own panel, and goes out over a channel the operator
 * trusts; a blurb that carried it would be pasted into whatever the operator
 * pastes blurbs into.
 */
export function handoverBlurb(params: HandoverBlurbParams): string {
  const { email, handle, brandName, studioUrl, quotaLimit, expiresAt } = params;
  const until = Number.isNaN(expiresAt.getTime())
    ? "the end of the agreed window"
    : BLURB_DATE_FORMAT.format(expiresAt);

  return [
    `Your advertiser account for ${brandName} is ready. Verify ${email}, then sign in at ${studioUrl} with the temporary password sent to you separately and change it. You publish as @${handle}. Outbound links are disclosed as paid placements.`,
    "",
    `Allowance: ${formatAllowance(quotaLimit)} until ${until}.`,
  ].join("\n");
}

/** HANDOVER_TIMING_NOTE is the line the operator reads, not the advertiser:
 * the password is inert until the account is verified. */
export const HANDOVER_TIMING_NOTE = "The password does nothing until they verify their email.";
